/**
 * Results Service
 * Handles weekly stats and results calculations
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { getSupabaseErrorMessage } from '@/lib/errorHandler';
import { startOfWeek, format, subWeeks } from 'date-fns';

export interface WeeklyStats {
  contacts_done: number;
  followups_scheduled: number;
  meetings_set: number;
  proposals_sent: number;
  won: number;
  lost: number;
  reactivated: number;
}

export interface WeeklyReport {
  id: string;
  workspace_id: string;
  user_id: string | null;
  week_start_date: string;
  stats: WeeklyStats;
  created_at: string;
  updated_at: string;
}

export class ResultsService {
  /**
   * Get week start date (Monday)
   */
  static getWeekStart(date: Date = new Date()): Date {
    return startOfWeek(date, { weekStartsOn: 1 });
  }

  /**
   * Calculate weekly stats from activity log
   */
  static async calculateWeeklyStats(
    workspaceId: string,
    userId: string | null,
    weekStart: Date
  ): Promise<WeeklyStats> {
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      
      // Use RPC function if available, otherwise calculate manually
      const { data, error } = await supabase.rpc('calculate_weekly_stats', {
        p_workspace_id: workspaceId,
        p_user_id: userId,
        p_week_start: weekStartStr,
      });

      if (error) {
        // Fallback to manual calculation
        logger.warn('[ResultsService] RPC failed, calculating manually', error);
        return await this.calculateWeeklyStatsManual(workspaceId, userId, weekStart);
      }

      return (data as WeeklyStats) || this.getEmptyStats();
    } catch (error: any) {
      logger.error('[ResultsService] Error calculating weekly stats', error, { workspaceId, userId, weekStart });
      return this.getEmptyStats();
    }
  }

  /**
   * Manual calculation fallback
   */
  private static async calculateWeeklyStatsManual(
    workspaceId: string,
    userId: string | null,
    weekStart: Date
  ): Promise<WeeklyStats> {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    let query = supabase
      .from('lead_activities')
      .select('type, outcome, next_step_type, meta')
      .eq('workspace_id', workspaceId)
      .gte('created_at', weekStart.toISOString())
      .lte('created_at', weekEnd.toISOString());

    if (userId) {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query;

    if (error) {
      logger.error('[ResultsService] Error fetching activities', error);
      return this.getEmptyStats();
    }

    const activities = data || [];

    return {
      contacts_done: activities.filter(a => 
        ['call', 'whatsapp', 'email'].includes(a.type || '') && a.outcome
      ).length,
      followups_scheduled: activities.filter(a => 
        ['follow_up_2d', 'follow_up_7d', 'schedule_meeting'].includes(a.next_step_type || '')
      ).length,
      meetings_set: activities.filter(a => a.outcome === 'meeting_set').length,
      proposals_sent: activities.filter(a => a.outcome === 'proposal_sent').length,
      won: activities.filter(a => a.outcome === 'won').length,
      lost: activities.filter(a => a.outcome === 'lost').length,
      reactivated: activities.filter(a => 
        a.type === 'note' && a.meta && (a.meta as any).reactivated === true
      ).length,
    };
  }

  /**
   * Get or create weekly report
   */
  static async getOrCreateWeeklyReport(
    workspaceId: string,
    userId: string | null,
    weekStart: Date
  ): Promise<WeeklyReport> {
    const weekStartStr = format(weekStart, 'yyyy-MM-dd');

    // Try to get existing report
    let query = supabase
      .from('weekly_reports')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('week_start_date', weekStartStr)
      .maybeSingle();

    if (userId) {
      query = query.eq('user_id', userId);
    } else {
      query = query.is('user_id', null);
    }

    const { data: existing, error: fetchError } = await query;

    if (fetchError && fetchError.code !== 'PGRST116') {
      logger.error('[ResultsService] Error fetching report', fetchError);
    }

    if (existing) {
      // Recalculate stats
      const stats = await this.calculateWeeklyStats(workspaceId, userId, weekStart);
      
      const { data: updated, error: updateError } = await supabase
        .from('weekly_reports')
        .update({ stats, updated_at: new Date().toISOString() })
        .eq('id', existing.id)
        .select()
        .single();

      if (updateError) {
        logger.error('[ResultsService] Error updating report', updateError);
        return { ...existing, stats } as WeeklyReport;
      }

      return updated as WeeklyReport;
    }

    // Create new report
    const stats = await this.calculateWeeklyStats(workspaceId, userId, weekStart);

    const { data: created, error: createError } = await supabase
      .from('weekly_reports')
      .insert({
        workspace_id: workspaceId,
        user_id: userId,
        week_start_date: weekStartStr,
        stats,
      })
      .select()
      .single();

    if (createError) {
      logger.error('[ResultsService] Error creating report', createError);
      throw new Error(getSupabaseErrorMessage(createError));
    }

    return created as WeeklyReport;
  }

  /**
   * Get weekly reports for comparison
   */
  static async getWeeklyReports(
    workspaceId: string,
    userId: string | null,
    weeks: number = 2
  ): Promise<WeeklyReport[]> {
    const reports: WeeklyReport[] = [];
    
    for (let i = 0; i < weeks; i++) {
      const weekStart = subWeeks(this.getWeekStart(), i);
      const report = await this.getOrCreateWeeklyReport(workspaceId, userId, weekStart);
      reports.push(report);
    }

    return reports;
  }

  /**
   * Get empty stats
   */
  private static getEmptyStats(): WeeklyStats {
    return {
      contacts_done: 0,
      followups_scheduled: 0,
      meetings_set: 0,
      proposals_sent: 0,
      won: 0,
      lost: 0,
      reactivated: 0,
    };
  }
}

